import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import stripeService from '../services/stripeService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Skip subscription check entirely for admin users (check both cases)
      if (currentUser?.role === 'ADMIN' || currentUser?.role === 'admin') {
        console.log('Admin user detected, skipping subscription check');
        setSubscription(null);
        setCustomer(null);
        setLoading(false);
        return;
      }
      
      // For CLIENT users, always use their UID (subscription is tied to user, not account)
      // For ADMIN users viewing a client account, use the account ID
      const userId = currentUser.uid;
      
      const data = await stripeService.getSubscriptionStatus(userId);
      
      setSubscription(data.subscription);
      setCustomer(data.customer);
    } catch (err) {
      console.error('Error loading subscription status:', err);
      // Don't set error for admins or when Stripe isn't configured yet
      if (currentUser?.role !== 'ADMIN') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load subscription status when user changes
  useEffect(() => {
    if (currentUser && currentUser.uid) {
      // For admin users, immediately set loading to false (check both cases)
      if (currentUser.role === 'ADMIN' || currentUser.role === 'admin') {
        console.log('Admin user detected, setting loading to false immediately');
        setLoading(false);
        setSubscription(null);
        setCustomer(null);
        return;
      }
      loadSubscriptionStatus();
    } else {
      setSubscription(null);
      setCustomer(null);
      setLoading(false);
    }
  }, [currentUser, loadSubscriptionStatus]);

  const refreshSubscription = () => {
    if (currentUser && currentUser.uid) {
      loadSubscriptionStatus();
    }
  };

  // Get subscription status for UI
  const getSubscriptionStatus = () => {
    return stripeService.getSubscriptionStatusForUI(subscription);
  };

  // Check if user can access dashboard
  const canAccessDashboard = () => {
    if (!currentUser) return false;
    
    // Admins always have access (check both cases)
    if (currentUser.role === 'ADMIN' || currentUser.role === 'admin') {
      console.log('Admin user has dashboard access');
      return true;
    }
    
    // Check subscription status for non-admin users
    const status = getSubscriptionStatus();
    return status.canAccess;
  };

  // Check if user needs to update payment method
  const needsPaymentUpdate = () => {
    const status = getSubscriptionStatus();
    return status.needsPayment;
  };

  // Check if user is in grace period
  const isInGracePeriod = () => {
    const status = getSubscriptionStatus();
    return status.inGracePeriod;
  };

  // Get days remaining in grace period
  const getGracePeriodDaysRemaining = () => {
    if (!subscription || !isInGracePeriod()) return 0;
    
    const currentTime = new Date().getTime();
    const gracePeriodEnd = new Date(subscription.current_period_end * 1000).getTime() + (15 * 24 * 60 * 60 * 1000);
    const daysRemaining = Math.ceil((gracePeriodEnd - currentTime) / (24 * 60 * 60 * 1000));
    
    return Math.max(0, daysRemaining);
  };

  // Start subscription process (for payment modal)
  const startSubscription = async (paymentMethod) => {
    try {
      if (!paymentMethod) {
        throw new Error('Payment method is required');
      }

      const result = await stripeService.createSubscription(
        currentUser.uid,
        currentUser.email,
        currentUser.displayName || 'User',
        paymentMethod.id
      );
      
      // Refresh subscription data after successful creation
      await loadSubscriptionStatus();
      
      return result;
    } catch (err) {
      console.error('Error starting subscription:', err);
      setError(err.message);
      throw err;
    }
  };


  // Open billing portal
  const openBillingPortal = async () => {
    try {
      if (!customer) {
        throw new Error('No customer found');
      }
      
      const returnUrl = `${window.location.origin}/dashboard/settings`;
      const session = await stripeService.createBillingPortalSession(
        customer.id,
        returnUrl
      );
      
      await stripeService.redirectToBillingPortal(session.url);
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err.message);
      throw err;
    }
  };

  const value = {
    subscription,
    customer,
    loading,
    error,
    refreshSubscription,
    getSubscriptionStatus,
    canAccessDashboard,
    needsPaymentUpdate,
    isInGracePeriod,
    getGracePeriodDaysRemaining,
    startSubscription,
    openBillingPortal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};